import { useEffect, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { queryString } from "capitalrx-utilities";

import "./i18n/i18n";
import { BasicRoot } from "@/basic";
import { useGetWhitelabel } from "@/api-hooks";
import { PremiumRoot } from "@/premium";
import { EnrollmentProvider } from "./state";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const Root = (): JSX.Element => {
  return (
    <QueryClientProvider client={queryClient}>
      <EnrollmentSwitch />
    </QueryClientProvider>
  );
};

const FORMULARY_ONLY_PATHS = ["freedom", "liberty"];

const EnrollmentSwitch = (): JSX.Element => {
  const search = window.location.search;
  const { client } = queryString.getQueryParams(search);
  const { i18n } = useTranslation();

  const pathParts = location.pathname.split("/");
  const potentialClientRedirect = pathParts[1];
  const isFormularyOnlyPath = FORMULARY_ONLY_PATHS.includes(potentialClientRedirect);
  const shouldRedirect = !client && potentialClientRedirect && !isFormularyOnlyPath && potentialClientRedirect !== "404";

  useEffect(() => {
    if (shouldRedirect) {
      window.location.href = `${window.location.protocol}//${window.location.host}/?client=${potentialClientRedirect}`;
    }
  }, [shouldRedirect, potentialClientRedirect]);

  const { data: experienceData, isLoading } = useGetWhitelabel(
    { client: client as string, language: i18n.language },
    { enabled: !isFormularyOnlyPath },
  );

  useEffect(() => {
    const favicon = experienceData?.theme?.favicon;
    if (favicon) {
      setFavicon(favicon);
    }
  }, [experienceData?.theme?.favicon]);

  const loadedRender = useMemo(() => {
    if (isFormularyOnlyPath) return <BasicRoot />;
    return (experienceData?.is_premium ? <PremiumRoot /> : <BasicRoot />);
  }, [experienceData?.is_premium, isFormularyOnlyPath]);

  if (!isFormularyOnlyPath && isLoading) return <div></div>;

  return (
    <EnrollmentProvider experienceData={experienceData}>
      {loadedRender}
    </EnrollmentProvider>
  );
};

const setFavicon = (faviconUrl: string) => {
  let link: HTMLLinkElement = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = faviconUrl;
};

const container = document.getElementById("root");
const root = createRoot(container);

if (root !== null) {
  root.render(<Root />);
} else {
  throw new Error("Unable to locate app root");
}
